// src/pages/lokasi/ActivePlantation.jsx
import React, { useState, useEffect, useRef } from "react";
import {
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    TablePagination,
    Chip,
    IconButton,
    MenuItem,
    Stack,
    Snackbar,
    FormControl,
    InputLabel,
    Select,
    Button,
    TextField,
    Tooltip
} from "@mui/material";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import SearchIcon from '@mui/icons-material/Search';

import { useTheme } from "@mui/material/styles";
import useAuthCheck from "../../hooks/useAuthCheck";
import { useOutletContext, useNavigate } from "react-router-dom";
import { database } from "../../firebase";
import { ref, onValue } from "firebase/database";

// Import utilitas importExcelHandler
//import { handleImportExcel, validateExcelFile } from "./utils/importExcelHandler";
import { handleImportExcel as importExcelFromUtils, validateExcelFile } from "./utils/importExcelHandler";
import { onValuePromise } from "./utils/importExcelHandler"; // Juga import helper

// Import formatters dari file utilitas
import {
    formatNumber,
    formatDecimal,
    formatDateDisplay,
    formatText
} from "../../utils/formatters-master";

// Import fungsi formatLastUpdated dari file terpisah
import { formatLastUpdated } from "../../utils/formatLastUpdated";

// Import CustomBreadcrumbs dari komponen terpisah
import CustomBreadcrumbs from "../../components/universal-features/breadcrumbs";

// Import CustomTableContainer dari komponen terpisah
import CustomTableContainer from "../../components/CustomScrollContainer";

// Helper function to format a YYYY-MM-DD date string to DD-MM-YYYY
const formatDateToDDMMYYYY = (dateString) => {
    if (!dateString || dateString === '' || dateString === '-') return '-';
    
    // Jika sudah format DD-MM-YYYY
    if (dateString.includes('-') && dateString.split('-').length === 3) {
        const parts = dateString.split('-');
        if (parts[0].length === 2 && parts[1].length === 2 && parts[2].length === 4) {
            return dateString;
        }
    }
    
    // Jika format YYYY-MM-DD
    if (dateString.includes('-') && dateString.split('-').length === 3) {
        const parts = dateString.split('-');
        if (parts[0].length === 4 && parts[1].length === 2 && parts[2].length === 2) {
            return `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
    }
    
    return dateString; // Return as is jika format tidak dikenali
};

function ActivePlantation() {
    const { user, loading: authLoading } = useAuthCheck();
    const theme = useTheme();
    const navigate = useNavigate();
    const { breadcrumbs } = useOutletContext();

    const [dataList, setDataList] = useState([]);
    const [originalDataList, setOriginalDataList] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const fileInputRef = useRef(null);
    const [importLoading, setImportLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });
    const [showFilterPanel, setShowFilterPanel] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [lastUpdated, setLastUpdated] = useState(null);

    // State untuk data konfigurasi dropdown
    const [kodeWilayahList, setKodeWilayahList] = useState([]);
    const [kepalaWilayahList, setKepalaWilayahList] = useState([]);
    const [kepalaKebunList, setKepalaKebunList] = useState([]);
    const [statusLokasiList, setStatusLokasiList] = useState([]);
    const [varietasBibitList, setVarietasBibitList] = useState([]);
    const [jenisBibitList, setJenisBibitList] = useState([]);
    const [kelasBibitList, setKelasBibitList] = useState([]);
    const [helperData, setHelperData] = useState({});

    // Filter states
    const [filterStatusLokasi, setFilterStatusLokasi] = useState('');
    const [filterKepalaWilayah, setFilterKepalaWilayah] = useState('');
    const [filterKepalaKebun, setFilterKepalaKebun] = useState('');
    const [filterJenisBibit, setFilterJenisBibit] = useState('');
    const [filterKelasBibit, setFilterKelasBibit] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            setLoadingData(true);
            try {
                // Fetch main data from db_freshpine/tb_lokasi
                const lokasiRef = ref(database, "db_freshpine/tb_lokasi");
                const lokasiSnapshot = await onValuePromise(lokasiRef);
                const lokasiData = lokasiSnapshot.val();
                const loadedLokasi = [];
                let latestUpdateTime = null;
                
                if (lokasiData) {
                    Object.keys(lokasiData).forEach(key => {
                        const record = lokasiData[key];
                        loadedLokasi.push({ firebaseKey: key, ...record });
                        
                        if (record.last_imported_at) {
                            const updateTime = new Date(record.last_imported_at);
                            if (!latestUpdateTime || updateTime > latestUpdateTime) {
                                latestUpdateTime = updateTime;
                            }
                        }
                    });
                    
                    if (latestUpdateTime) {
                        setLastUpdated(latestUpdateTime);
                    }
                }
                // Filter data to only show 'active' status_plantation by default
                const activeData = loadedLokasi.filter(item => item.status_plantation !== 'closing');
                setOriginalDataList(activeData);
                setDataList(activeData);

                // Fetch lookup data untuk berbagai dropdown
                await fetchLookupData();

            } catch (error) {
                console.error("Error fetching data:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal memuat data.",
                    severity: "error",
                });
            } finally {
                setLoadingData(false);
            }
        };

        if (user) {
            fetchData();
        }
    }, [user]);

    // Fungsi untuk mengambil data lookup
    const fetchLookupData = async () => {
        try {
            // Fetch lookup data for Kode Wilayah
            const kodeWilayahConfigRef = ref(database, "db_config/db_fresh_pineapple/tb_kode_wilayah");
            const kodeWilayahConfigSnapshot = await onValuePromise(kodeWilayahConfigRef);
            const kodeWilayahConfigData = kodeWilayahConfigSnapshot.val();
            if (kodeWilayahConfigData) {
                const list = Object.keys(kodeWilayahConfigData).map(key => ({
                    id: key,
                    nama_kode_wilayah: kodeWilayahConfigData[key].nama_kode_wilayah,
                    id_kepala_wilayah: kodeWilayahConfigData[key].id_kepala_wilayah || ''
                }));
                setKodeWilayahList(list);
            }

            // Fetch data untuk Kepala Wilayah
            const kepalaWilayahConfigRef = ref(database, "db_config/db_fresh_pineapple/tb_kepala_wilayah");
            const kepalaWilayahConfigSnapshot = await onValuePromise(kepalaWilayahConfigRef);
            const kepalaWilayahConfigData = kepalaWilayahConfigSnapshot.val();
            if (kepalaWilayahConfigData) {
                const list = Object.keys(kepalaWilayahConfigData).map(key => ({
                    id: key,
                    nama_kepala_wilayah: kepalaWilayahConfigData[key].nama_kepala_wilayah
                }));
                setKepalaWilayahList(list);
            }

            // Fetch lookup data for Kepala Kebun
            const kepalaKebunConfigRef = ref(database, "db_config/db_fresh_pineapple/tb_kepala_kebun");
            const kepalaKebunConfigSnapshot = await onValuePromise(kepalaKebunConfigRef);
            const kepalaKebunConfigData = kepalaKebunConfigSnapshot.val();
            if (kepalaKebunConfigData) {
                const list = Object.keys(kepalaKebunConfigData).map(key => ({
                    id: key,
                    nama_kepala_kebun: kepalaKebunConfigData[key].nama_kepala_kebun
                }));
                setKepalaKebunList(list);
            }

            // Fetch lookup data for Status Lokasi
            const statusLokasiConfigRef = ref(database, "db_config/db_fresh_pineapple/tb_status_lokasi");
            const statusLokasiConfigSnapshot = await onValuePromise(statusLokasiConfigRef);
            const statusLokasiConfigData = statusLokasiConfigSnapshot.val();
            if (statusLokasiConfigData) {
                const list = Object.keys(statusLokasiConfigData).map(key => ({
                    id: key,
                    nama_status_lokasi: statusLokasiConfigData[key].nama_status_lokasi,
                    indicator: statusLokasiConfigData[key].indicator || '#FFFFFF'
                }));
                setStatusLokasiList(list);
            }

            // Fetch lookup data for Varietas Bibit
            const varietasBibitConfigRef = ref(database, "db_config/db_fresh_pineapple/tb_varietas_bibit");
            const varietasBibitConfigSnapshot = await onValuePromise(varietasBibitConfigRef);
            const varietasBibitConfigData = varietasBibitConfigSnapshot.val();
            if (varietasBibitConfigData) {
                const list = Object.keys(varietasBibitConfigData).map(key => ({
                    id: key,
                    nama_varietas_bibit: varietasBibitConfigData[key].nama_varietas_bibit
                }));
                setVarietasBibitList(list);
            }

            // Fetch lookup data for Jenis Bibit
            const jenisBibitConfigRef = ref(database, "db_config/db_fresh_pineapple/tb_jenis_bibit");
            const jenisBibitConfigSnapshot = await onValuePromise(jenisBibitConfigRef);
            const jenisBibitConfigData = jenisBibitConfigSnapshot.val();
            if (jenisBibitConfigData) {
                const list = Object.keys(jenisBibitConfigData).map(key => ({
                    id: key,
                    nama_jenis_bibit: jenisBibitConfigData[key].nama_jenis_bibit
                }));
                setJenisBibitList(list);
            }

            // Fetch lookup data for Kelas Bibit
            const kelasBibitConfigRef = ref(database, "db_config/db_fresh_pineapple/tb_kelas_bibit");
            const kelasBibitConfigSnapshot = await onValuePromise(kelasBibitConfigRef);
            const kelasBibitConfigData = kelasBibitConfigSnapshot.val();
            if (kelasBibitConfigData) {
                const list = Object.keys(kelasBibitConfigData).map(key => ({
                    id: key,
                    nama_kelas_bibit: kelasBibitConfigData[key].nama_kelas_bibit
                }));
                setKelasBibitList(list);
            }

            // Fetch data from db_config/db_helper
            const helperRef = ref(database, "db_config/db_helper/tb_wilayah_plant_care");
            const helperSnapshot = await onValuePromise(helperRef);
            const helperRawData = helperSnapshot.val();
            if (helperRawData) {
                setHelperData(helperRawData);
            } else {
                setHelperData({});
            }

        } catch (error) {
            console.error("Error fetching lookup data:", error);
        }
    };

    // Effect untuk melakukan pencarian saat searchQuery berubah
    useEffect(() => {
        if (searchQuery) {
            const queryLower = searchQuery.toLowerCase();
            const filtered = originalDataList.filter(item => 
                (item.lokasi && item.lokasi.toLowerCase().includes(queryLower)) ||
                (item.wilayah && item.wilayah.toLowerCase().includes(queryLower)) ||
                (item.status && item.status.toLowerCase().includes(queryLower)) ||
                (item.bibit && item.bibit.toLowerCase().includes(queryLower)) ||
                (item.deskripsi && item.deskripsi.toLowerCase().includes(queryLower)) ||
                (item.desc_lvl_01 && item.desc_lvl_01.toLowerCase().includes(queryLower)) ||
                (item.desc_lvl_02 && item.desc_lvl_02.toLowerCase().includes(queryLower)) ||
                (item.desc_lvl_03 && item.desc_lvl_03.toLowerCase().includes(queryLower))
            );
            setDataList(filtered);
        } else {
            setDataList(originalDataList);
        }
        setPage(0);
    }, [searchQuery, originalDataList]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleMenuClick = (event) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    //const handleImportButtonClick = () => {
    //    if (fileInputRef.current) {
    //        fileInputRef.current.click();
    //    }
    //};
    const handleImportButtonClick = () => {
    if (!fileInputRef.current) {
        console.error("File input reference is not available");
        setSnackbar({
            open: true,
            message: "Sistem file tidak tersedia. Silakan refresh halaman.",
            severity: "error",
        });
        return;
    }
    
    try {
        fileInputRef.current.click();
    } catch (error) {
        console.error("Error clicking file input:", error);
        setSnackbar({
            open: true,
            message: "Gagal membuka dialog pemilihan file",
            severity: "error",
        });
    }
};

{/*
    const handleImportExcel = async (event) => {
        const file = event.target.files[0];
        if (!file) {
            setSnackbar({
                open: true,
                message: "Tidak ada file yang dipilih.",
                severity: "warning",
            });
            return;
        }

        setImportLoading(true);

        try {
            // Validasi file Excel
            await validateExcelFile(file);
            
            // Panggil fungsi import dari utilitas
            await handleImportExcel(file, {
                onStart: (message) => {
                    setSnackbar({
                        open: true,
                        message,
                        severity: "info",
                    });
                },
                onSuccess: (results) => {
                    setSnackbar({
                        open: true,
                        message: `Impor selesai! Berhasil menambahkan ${results.importedCount} data baru dan memperbarui ${results.updatedCount} data. ${results.ignoredCount} data diabaikan.`,
                        severity: "success",
                    });
                },
                onError: (error) => {
                    setSnackbar({
                        open: true,
                        message: error.message || "Terjadi kesalahan saat mengimpor data dari Excel.",
                        severity: "error",
                    });
                },
                onComplete: () => {
                    setImportLoading(false);
                },
                updateDataList: (activeData) => {
                    setDataList(activeData);
                    setOriginalDataList(activeData);
                },
                updateLastUpdated: (date) => {
                    setLastUpdated(date);
                }
            });

        } catch (error) {
            console.error("Gagal impor Excel:", error);
            setSnackbar({
                open: true,
                message: error.message || "Terjadi kesalahan saat mengimpor data.",
                severity: "error",
            });
            setImportLoading(false);
        } finally {
            event.target.value = null;
            handleMenuClose();
        }
    };
*/}

const handleFileSelect = async (event) => {
    // Validasi event
    if (!event || !event.target) {
        console.error("Event or event.target is undefined");
        setSnackbar({
            open: true,
            message: "Terjadi kesalahan: Sistem file tidak merespon",
            severity: "error",
        });
        return;
    }

    const files = event.target.files;
    
    if (!files || files.length === 0) {
        console.warn("User cancelled file selection");
        // Tidak perlu snackbar, karena user mungkin sengaja membatalkan
        return;
    }

    const file = files[0];
    
    setImportLoading(true);

    try {
        // Validasi file Excel
        await validateExcelFile(file);
        
        // Panggil fungsi import dari utilitas (gunakan nama baru)
        await importExcelFromUtils(file, {
            onStart: (message) => {
                setSnackbar({
                    open: true,
                    message,
                    severity: "info",
                });
            },
            onSuccess: (results) => {
                setSnackbar({
                    open: true,
                    message: `Impor selesai! Berhasil menambahkan ${results.importedCount} data baru dan memperbarui ${results.updatedCount} data. ${results.ignoredCount} data diabaikan.`,
                    severity: "success",
                });
            },
            onError: (error) => {
                setSnackbar({
                    open: true,
                    message: error.message || "Terjadi kesalahan saat mengimpor data dari Excel.",
                    severity: "error",
                });
            },
            onComplete: () => {
                setImportLoading(false);
            },
            updateDataList: (activeData) => {
                setDataList(activeData);
                setOriginalDataList(activeData);
            },
            updateLastUpdated: (date) => {
                setLastUpdated(date);
            }
        });

    } catch (error) {
        console.error("Gagal impor Excel:", error);
        setSnackbar({
            open: true,
            message: error.message || "Terjadi kesalahan saat mengimpor data.",
            severity: "error",
        });
        setImportLoading(false);
    } finally {
        // Reset input file
        if (event.target) {
            event.target.value = null;
        }
        handleMenuClose();
    }
};

    const handleExport = () => {
        setSnackbar({
            open: true,
            message: "Fungsi Export akan dikembangkan.",
            severity: "info",
        });
        handleMenuClose();
    };

    const handlePrint = () => {
        setSnackbar({
            open: true,
            message: "Fungsi Print akan dikembangkan.",
            severity: "info",
        });
        handleMenuClose();
    };

    const handleToggleFilterPanel = () => {
        setShowFilterPanel(prev => !prev);
    };

    const handleFilterData = () => {
        let filtered = [...originalDataList];

        if (filterStatusLokasi) {
            filtered = filtered.filter(item => item.status === filterStatusLokasi);
        }
        if (filterKepalaWilayah) {
            const selectedKepalaWilayahId = kepalaWilayahList.find(kw => kw.nama_kepala_wilayah === filterKepalaWilayah)?.id;
            if (selectedKepalaWilayahId) {
                filtered = filtered.filter(item => {
                    const kodeWilayahItem = kodeWilayahList.find(kw => kw.nama_kode_wilayah === item.wilayah);
                    return kodeWilayahItem?.id_kepala_wilayah === selectedKepalaWilayahId;
                });
            }
        }
        if (filterKepalaKebun) {
            filtered = filtered.filter(item => {
                const helperEntry = helperData[item.lokasi];
                if (helperEntry && helperEntry.id_kepala_kebun) {
                    const kepalaKebunName = kepalaKebunList.find(kk => kk.id === helperEntry.id_kepala_kebun)?.nama_kepala_kebun;
                    return kepalaKebunName === filterKepalaKebun;
                }
                return false;
            });
        }
        if (filterJenisBibit) {
            filtered = filtered.filter(item => {
                const bibitString = item.bibit || '';
                const jenis = bibitString.length >= 6 ? bibitString.substring(5, 6) : 'N/A';
                return jenis === filterJenisBibit;
            });
        }
        if (filterKelasBibit) {
            filtered = filtered.filter(item => {
                const bibitString = item.bibit || '';
                const kelas = bibitString.length >= 7 ? bibitString.substring(6, 7) : 'N/A';
                return kelas === filterKelasBibit;
            });
        }

        setDataList(filtered);
        setPage(0);
    };

    const handleResetFilter = () => {
        setFilterStatusLokasi('');
        setFilterKepalaWilayah('');
        setFilterKepalaKebun('');
        setFilterJenisBibit('');
        setFilterKelasBibit('');
        setDataList(originalDataList);
        setPage(0);
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    // Fungsi untuk mengekstrak informasi bibit berdasarkan status
    const extractBibitInfo = (status, bibitString) => {
        // Ambil 2 karakter pertama dari status
        const statusPrefix = (status || '').substring(0, 2).toUpperCase();
        
        // Cek apakah status diawali dengan NF atau NL
        const isNForNL = statusPrefix === 'NF' || statusPrefix === 'NL';
        
        // Jika bukan NF atau NL, kembalikan tanda "-" untuk semua kolom
        if (!isNForNL) {
            return {
                varietas: '-',
                jenis: '-',
                kelas: '-'
            };
        }
        
        // Jika NF atau NL, tapi bibit string kosong, beri nilai default
        if (!bibitString || bibitString.trim() === '') {
            return {
                varietas: 'MD2',
                jenis: 'Ratoon',
                kelas: 'Ratoon'
            };
        }
        
        // Jika NF atau NL dan ada bibit string, ekstrak informasi
        return {
            varietas: bibitString.length >= 5 ? bibitString.substring(2, 5) : 'N/A',
            jenis: bibitString.length >= 6 ? bibitString.substring(5, 6) : 'N/A',
            kelas: bibitString.length >= 7 ? bibitString.substring(6, 7) : 'N/A'
        };
    };

    if (authLoading || loadingData) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    Anda harus login untuk mengakses halaman ini
                </Alert>
            </Box>
        );
    }

    const displayedDataList = dataList;

    return (
        <Box sx={{ pb: 3 }}>
            {/* Breadcrumbs - Menggunakan CustomBreadcrumbs */}
            <CustomBreadcrumbs breadcrumbItems={breadcrumbs} />

            {showFilterPanel && (
                <Paper sx={{ width: '100%', overflow: 'hidden', p: 2, mb: 2 }}>
                    <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
                        Filter
                    </Typography>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Status Lokasi</InputLabel>
                            <Select
                                value={filterStatusLokasi}
                                label="Status Lokasi"
                                onChange={(e) => setFilterStatusLokasi(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {statusLokasiList.map((status) => (
                                    <MenuItem key={status.id} value={status.nama_status_lokasi}>
                                        {status.nama_status_lokasi}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small">
                            <InputLabel>Kepala Wilayah</InputLabel>
                            <Select
                                value={filterKepalaWilayah}
                                label="Kepala Wilayah"
                                onChange={(e) => setFilterKepalaWilayah(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {kepalaWilayahList.map((kw) => (
                                    <MenuItem key={kw.id} value={kw.nama_kepala_wilayah}>
                                        {kw.nama_kepala_wilayah}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small">
                            <InputLabel>Kepala Kebun</InputLabel>
                            <Select
                                value={filterKepalaKebun}
                                label="Kepala Kebun"
                                onChange={(e) => setFilterKepalaKebun(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {kepalaKebunList.map((kk) => (
                                    <MenuItem key={kk.id} value={kk.nama_kepala_kebun}>
                                        {kk.nama_kepala_kebun}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small">
                            <InputLabel>Jenis Bibit</InputLabel>
                            <Select
                                value={filterJenisBibit}
                                label="Jenis Bibit"
                                onChange={(e) => setFilterJenisBibit(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {jenisBibitList.map((jb) => (
                                    <MenuItem key={jb.id} value={jb.nama_jenis_bibit.charAt(0)}>
                                        {jb.nama_jenis_bibit}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small">
                            <InputLabel>Kelas Bibit</InputLabel>
                            <Select
                                value={filterKelasBibit}
                                label="Kelas Bibit"
                                onChange={(e) => setFilterKelasBibit(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {kelasBibitList.map((kb) => (
                                    <MenuItem key={kb.id} value={kb.nama_kelas_bibit.charAt(0)}>
                                        {kb.nama_kelas_bibit}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                        <Button variant="outlined" onClick={handleResetFilter}>
                            Reset Filter
                        </Button>
                        <Button variant="contained" onClick={handleFilterData}>
                            Filter Data
                        </Button>
                    </Stack>
                </Paper>
            )}

            <Paper sx={{ width: '100%', overflow: 'hidden', p: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Typography variant="h6" component="h2">
                        Location Status
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                            size="small"
                            placeholder="Cari lokasi, wilayah, status, deskripsi..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                            }}
                            sx={{ width: 300 }}
                        />
                        <Tooltip title="Filter">
                            <IconButton color="primary" size="small" onClick={handleToggleFilterPanel}>
                                {showFilterPanel ? <FilterAltOutlinedIcon /> : <FilterAltOffOutlinedIcon />}
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Import Excel">
                            <IconButton color="primary" size="small" onClick={handleImportButtonClick} disabled={importLoading}>
                                {importLoading ? <CircularProgress size={20} /> : <FileUploadOutlinedIcon />}
                            </IconButton>
                        </Tooltip>
                        {/*
                        <input
                            type="file"
                            accept=".xlsx, .xls"
                            ref={fileInputRef}
                            style={{ display: "none" }}
                            onChange={handleImportExcel}
                        />
                        */}
                        
<input
    type="file"
    accept=".xlsx, .xls"
    ref={fileInputRef}
    style={{ display: "none" }}
    onChange={handleFileSelect}
/>
                        <Tooltip title="Export Data">
                            <IconButton color="primary" size="small" onClick={handleExport}>
                                <FileDownloadOutlinedIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Print">
                            <IconButton color="primary" size="small" onClick={handlePrint}>
                                <PrintOutlinedIcon />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>

                {displayedDataList.length > 0 ? (
                    <>
                        {/* Gunakan CustomTableContainer */}
                        <CustomTableContainer
                            sx={{ 
                                maxHeight: 500,
                                border: '1px solid #e0e0e0',
                                borderRadius: 1
                            }}
                            hoverScrollbar={true}
                            scrollbarHeight="8px"
                            scrollbarWidth="8px"
                            scrollbarThumbColor="#c1c1c1"
                            scrollbarHoverColor="#a8a8a8"
                        >
                            <Table stickyHeader aria-label="active plantation table">
                                <TableHead>
                                    {/* Baris Pertama - Header Utama */}
                                    <TableRow>
                                        {/* Info Lokasi Headers (5 kolom) */}
                                        <TableCell rowSpan={2} className="ded-tch">Lokasi</TableCell>
                                        <TableCell rowSpan={2} className="ded-tch">Status</TableCell>
                                        {/* Komentar untuk kolom yang disembunyikan */}
                                        {/* <TableCell rowSpan={2} className="ded-tch">Kepala Wilayah</TableCell> */}
                                        {/* <TableCell rowSpan={2} className="ded-tch">Kepala Kebun</TableCell> */}
                                        <TableCell rowSpan={2} className="ded-tch">Kode Wilayah</TableCell>
                                        
                                        {/* Bibit - 3 kolom */}
                                        <TableCell colSpan={3} className="ded-tch">
                                            BIBIT
                                        </TableCell>
                                        
                                        {/* Luas - 3 kolom */}
                                        <TableCell colSpan={3} className="ded-tch">
                                            LUAS
                                        </TableCell>
                                        
                                        {/* Tanggal - 3 kolom */}
                                        <TableCell colSpan={3} className="ded-tch">
                                            Tanggal
                                        </TableCell>
                                    </TableRow>
                                    
                                    {/* Baris Kedua - Subheaders */}
                                    <TableRow className="tr">
                                        {/* Bibit Subheaders (3 kolom) */}
                                        <TableCell className="ded-tc">Varietas</TableCell>
                                        <TableCell className="ded-tc">Jenis</TableCell>
                                        <TableCell className="ded-tc">Kelas</TableCell>
                                        
                                        {/* Luas Subheaders (3 kolom) */}
                                        <TableCell className="ded-tc">Bruto</TableCell>
                                        <TableCell className="ded-tc">Netto</TableCell>
                                        <TableCell className="ded-tc">Aktif</TableCell>
                                        
                                        {/* Tanggal Subheaders (3 kolom) */}
                                        <TableCell className="ded-tc">Mulai Tanam</TableCell>
                                        <TableCell className="ded-tc">Mulai Rawat</TableCell>
                                        <TableCell className="ded-tc">Perubahan Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {displayedDataList
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((row) => {
                                            // Lookup nama_kode_wilayah
                                            const kodeWilayahItem = kodeWilayahList.find(
                                                (item) => item.nama_kode_wilayah === row.wilayah
                                            );
                                            const displayKodeWilayah = kodeWilayahItem?.nama_kode_wilayah || row.wilayah || 'N/A';

                                            // Lookup nama_kepala_wilayah berdasarkan id_kepala_wilayah dari kodeWilayahItem
                                            const displayKepalaWilayah = kodeWilayahItem?.id_kepala_wilayah
                                                ? kepalaWilayahList.find(
                                                    (item) => item.id === kodeWilayahItem.id_kepala_wilayah
                                                )?.nama_kepala_wilayah || 'N/A'
                                                : 'N/A';

                                            // Lookup nama_kepala_kebun based on lokasi from helperData
                                            let displayKepalaKebun = 'N/A';
                                            if (helperData[row.lokasi] && helperData[row.lokasi].id_kepala_kebun) {
                                                const kepalaKebunIdFromHelper = helperData[row.lokasi].id_kepala_kebun;
                                                const kepalaKebunFound = kepalaKebunList.find(
                                                    (item) => item.id === kepalaKebunIdFromHelper
                                                );
                                                displayKepalaKebun = kepalaKebunFound?.nama_kepala_kebun || 'N/A';
                                            }

                                            // Lookup nama_status_lokasi dan indicator
                                            const statusLokasiItem = statusLokasiList.find(
                                                (item) => item.nama_status_lokasi === row.status
                                            );
                                            const displayStatusLokasi = statusLokasiItem?.nama_status_lokasi || row.status || 'N/A';
                                            const statusIndicatorColor = statusLokasiItem?.indicator || '#FFFFFF';

                                            // Ekstrak informasi bibit menggunakan fungsi helper
                                            const bibitInfo = extractBibitInfo(row.status, row.bibit);

                                            return (
                                                <TableRow key={row.firebaseKey}>
                                                    {/* Info Lokasi - 5 kolom */}
                                                    <TableCell className="ded-tbtrtc">
                                                        <Box>
                                                            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                                                {row.lokasi || 'N/A'}
                                                            </div>
                                                            <div style={{ fontSize: '0.8em', color: '#666' }}>
                                                                ID: {row.id_lokasi || '-'}
                                                            </div>
                                                            <div style={{ fontSize: '0.8em', color: '#666' }}>
                                                                Key: {row.firebaseKey || '-'}
                                                            </div>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        {displayStatusLokasi ? (
                                                            <Chip
                                                                label={displayStatusLokasi}
                                                                style={{ 
                                                                    backgroundColor: statusIndicatorColor, 
                                                                    color: theme.palette.getContrastText(statusIndicatorColor) 
                                                                }}
                                                                size="small"
                                                            />
                                                        ) : 'N/A'}
                                                    </TableCell>

                                                    {/* Kolom yang disembunyikan */}
                                                    {/* <TableCell>{displayKepalaWilayah}</TableCell> */}
                                                    {/* <TableCell>{displayKepalaKebun}</TableCell> */}

                                                    <TableCell>{displayKodeWilayah}</TableCell>
                                                    
                                                    {/* Bibit - 3 kolom dengan logika NF/NL */}
                                                    <TableCell>{bibitInfo.varietas}</TableCell>
                                                    <TableCell>{bibitInfo.jenis}</TableCell>
                                                    <TableCell>{bibitInfo.kelas}</TableCell>
                                                    
                                                    {/* Luas - 3 kolom - Menampilkan sebagai string */}
                                                    <TableCell>{row.bruto || '-'}</TableCell>
                                                    <TableCell>{row.netto || '-'}</TableCell>
                                                    <TableCell>{row.aktif || '-'}</TableCell>
                                                    
                                                    {/* Tanggal - 3 kolom - Langsung tampilkan format DD-MM-YYYY dari Firebase */}
                                                    <TableCell>{formatDateToDDMMYYYY(row.mulai_tanam)}</TableCell>
                                                    <TableCell>{formatDateToDDMMYYYY(row.mulai_rawat)}</TableCell>
                                                    <TableCell>{formatDateToDDMMYYYY(row.perubahan_status)}</TableCell>
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                            </Table>
                        </CustomTableContainer>
                        
                        {/* Container untuk TablePagination dan Last Updated Info */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                            {/* Bagian kiri: Info Data diupdate terakhir */}
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                Data diupdate terakhir: {formatLastUpdated(lastUpdated)}
                            </Typography>
                            
                            {/* Bagian kanan: TablePagination */}
                            <TablePagination
                                rowsPerPageOptions={[10, 25, 50, 100]}
                                component="div"
                                count={displayedDataList.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                sx={{ 
                                    flexShrink: 0,
                                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                        margin: 0
                                    }
                                }}
                            />
                        </Box>
                    </>
                ) : (
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        minHeight: 200,
                        border: '1px dashed #e0e0e0',
                        borderRadius: 1
                    }}>
                        <Typography variant="body1" color="text.secondary">
                            {searchQuery ? 'Tidak ada data yang sesuai dengan pencarian' : 'Tidak ada data lokasi aktif.'}
                        </Typography>
                    </Box>
                )}
            </Paper>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default ActivePlantation;